<template>
  <div class="station-form-modern">
    <el-form
      ref="formRef"
      :model="form"
      :rules="rules"
      label-position="top"
      @submit.prevent="submitForm"
      class="station-form-modern__form"
    >
      <section class="form-section form-section--basic">
        <div class="form-section__grid">
          <el-row :gutter="12" class="form-row-mobile">
            <el-col :span="7" :xs="12">
              <el-form-item label="中文名称" prop="chinese_name" class="form-item--compact">
                <el-input
                  v-model="form.chinese_name"
                  placeholder="请输入中文名称"
                  @input="onChineseNameChange"
                  @blur="generateEnglishName"
                  size="small"
                />
              </el-form-item>
            </el-col>
            <el-col :span="7" :xs="12">
              <el-form-item label="英文名称" prop="english_name" class="form-item--compact">
                <el-input
                  v-model="form.english_name"
                  placeholder="自动生成或手动输入"
                  size="small"
                >
                  <template #suffix>
                    <el-tooltip
                      placement="top"
                      content="根据中文名称自动生成英文名称"
                      effect="light"
                    >
                      <button
                        type="button"
                        class="generate-english-btn"
                        @click="generateEnglishName"
                      >
                        <el-icon><Refresh /></el-icon>
                      </button>
                    </el-tooltip>
                  </template>
                </el-input>
              </el-form-item>
            </el-col>
            <el-col :span="5" :xs="12" v-if="station">
              <el-form-item label="代码" prop="station_code" class="form-item--compact">
                <el-input
                  v-model="form.station_code"
                  placeholder="ABC"
                  maxlength="4"
                  style="font-family: monospace; text-transform: uppercase; width: 100%;"
                  @input="onStationCodeInput"
                  @blur="onStationCodeBlur"
                  size="small"
                />
              </el-form-item>
            </el-col>
            <el-col :span="5" :xs="12" :offset="station ? 0 : 5">
              <el-form-item label="快速创建" class="form-item--compact">
                <div class="quick-toggle-inline">
                  <el-switch
                    v-model="form.is_quick_added"
                    active-text="是"
                    inactive-text="否"
                    @change="onQuickAddToggle"
                  />
                </div>
              </el-form-item>
            </el-col>
          </el-row>
        </div>
        <div class="status-region-row">
          <div class="status-col">
            <el-form-item
              label="车站状态"
              prop="operation_status"
              class="form-item--compact"
            >
              <div class="status-selector">
                <button
                  v-for="option in stationStatusOptions"
                  :key="option.value"
                  type="button"
                  class="status-chip"
                  :class="[
                    `status-chip--${option.type}`,
                    { 'is-active': form.operation_status === option.value }
                  ]"
                  @click="setOperationStatus(option.value)"
                >
                  <span class="status-chip__dot"></span>
                  {{ option.label }}
                </button>
              </div>
            </el-form-item>
          </div>
          <div class="region-col">
            <el-form-item label="所属行政区划" class="form-item--compact">
              <div class="region-field">
                <el-select
                  v-model="form.region_id"
                  :placeholder="regionPlaceholder"
                  filterable
                  clearable
                  :disabled="regionSelectDisabled"
                  :loading="regionsLoading"
                  style="width: 100%;"
                  @change="onRegionChange"
                >
                  <el-option
                    v-for="region in availableRegions"
                    :key="region.id"
                    :label="`${region.chinese_name}${region.english_name ? ' | ' + region.english_name : ''}`"
                    :value="region.id"
                  >
                    <div class="region-option">
                      <span class="region-option__name">{{ region.chinese_name }}</span>
                      <span v-if="region.english_name" class="region-option__sub">{{ region.english_name }}</span>
                    </div>
                  </el-option>
                </el-select>
                <div class="region-hint">
                  <el-tag v-if="selectedRegion" size="small" type="info" effect="plain">
                    {{ selectedRegion.chinese_name }}
                    <span v-if="selectedRegion.english_name"> / {{ selectedRegion.english_name }}</span>
                  </el-tag>
                  <span class="region-hint__text">{{ regionHintText }}</span>
                </div>
              </div>
            </el-form-item>
          </div>
        </div>
        <el-alert
          v-if="form.is_quick_added"
          title="快速添加车站模式"
          description="仅需填写中英文名与所属线路；坐标与形状可留空。切换为普通车站以编辑坐标与形状。"
          type="info"
          :closable="false"
          class="quick-mode-alert"
        />
        <el-form-item
          v-if="form.is_quick_added"
          label="中心坐标（可选）"
          class="form-item--compact quick-center-item"
        >
          <el-input
            v-model="quickCenterInput"
            placeholder="例如：123 456 或 123 64 456"
            size="small"
          />
          <div class="quick-center-hint">
            X、Z 必填；Y 可不填（默认 64）。留空则不在地图显示，但仍可加入线路。
          </div>
        </el-form-item>
      </section>

    <!-- 车站形状设计器（普通车站才显示） -->
    <el-card v-if="!form.is_quick_added" class="shape-designer" shadow="hover">
      <template #header>
        <div class="card-header">
          <span style="font-size: 14px;">🎯 车站形状设计器</span>
          <el-radio-group v-model="shapeMode" size="small" @change="onShapeModeChange">
            <el-radio-button value="rectangle">📐 快速矩形</el-radio-button>
            <el-radio-button value="custom">✏️ 自定义形状</el-radio-button>
          </el-radio-group>
        </div>
      </template>

      <!-- 快速矩形模式 -->
      <div v-if="shapeMode === 'rectangle'" class="rectangle-mode">
        <el-form-item label="矩形对角线" class="form-item--full" style="margin-bottom: 4px;">
          <div class="rectangle-inputs-row">
            <div class="rectangle-inputs">
              <el-input-number
                v-model="rectangleCoords.x1"
                :controls="false"
                :step="1"
                placeholder="x1"
              />
              <el-input-number
                v-model="rectangleCoords.z1"
                :controls="false"
                :step="1"
                placeholder="z1"
              />
              <span class="height-split">→</span>
              <el-input-number
                v-model="rectangleCoords.x2"
                :controls="false"
                :step="1"
                placeholder="x2"
              />
              <el-input-number
                v-model="rectangleCoords.z2"
                :controls="false"
                :step="1"
                placeholder="z2"
              />
            </div>
            <el-button type="primary" size="small" class="rectangle-action" @click="calculateRectangleShape">
              生成矩形
            </el-button>
          </div>
        </el-form-item>

        <el-row :gutter="12" style="margin-top: 4px;">
          <el-col :span="14">
            <el-form-item label="高度范围" required style="margin-bottom: 2px;">
              <div class="height-inputs">
                <el-input-number
                  v-model="form.shape_y"
                  :controls="false"
                  :step="1"
                  :min="0"
                  :max="320"
                  placeholder="标记显示高度"
                  style="width: 110px;"
                />
                <span class="height-split">~</span>
                <el-input-number
                  v-model="form.center_y"
                  :controls="false"
                  :step="1"
                  placeholder="车站Y坐标"
                  style="width: 110px;"
                />
              </div>
            </el-form-item>
          </el-col>
          <el-col :span="10">
            <el-form-item style="margin-bottom: 2px;">
              <template #label>
                <span style="display: flex; align-items: center; gap: 4px;">
                  深度测试
                  <el-tooltip
                    placement="top"
                    effect="light"
                    :show-after="200"
                  >
                    <template #content>
                      <div style="max-width: 260px; line-height: 1.5;">
                        如果此值为 false，标记将始终渲染在所有其他（高分辨率）地形之上。<br/>
                        如果为 true，当高分辨率图块位于标记前面时，它们将能够覆盖标记。
                      </div>
                    </template>
                    <el-icon :size="14" style="color: #909399; cursor: help;">
                      <QuestionFilled />
                    </el-icon>
                  </el-tooltip>
                </span>
              </template>
              <div class="depth-test-wrapper">
                <el-switch
                  v-model="form.depth_test"
                  active-text="开启"
                  inactive-text="关闭"
                />
              </div>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 顶点列表显示 -->
        <el-form-item label="顶点列表" class="form-item--full" style="margin-top: 8px;">
          <div class="vertices-editor">
            <div class="vertex-row vertex-header">
              <span>X</span>
              <span>Z</span>
              <span class="vertex-actions">操作</span>
            </div>
            <div
              v-for="(coord, index) in form.shape_coordinates"
              :key="index"
              class="vertex-row"
            >
              <el-input-number
                v-model="coord.x"
                :controls="false"
                :step="1"
              />
              <el-input-number
                v-model="coord.z"
                :controls="false"
                :step="1"
              />
              <div class="vertex-actions">
                <el-button
                  size="small"
                  :icon="Delete"
                  type="danger"
                  circle
                  @click="removeShapePoint(index)"
                  :disabled="form.shape_coordinates.length <= 3"
                />
              </div>
            </div>
            <div class="vertex-footer">
              <el-button size="small" type="primary" @click="addShapePoint">新增顶点</el-button>
            </div>
          </div>
        </el-form-item>
      </div>

      <!-- 自定义形状模式 -->
      <div v-else-if="shapeMode === 'custom'" class="custom-mode">
        <el-form-item label="中心坐标" class="form-item--full" style="margin-bottom: 4px;">
          <div class="rectangle-inputs">
            <div class="rectangle-row">
              <el-input-number
                v-model="form.center_x"
                :controls="false"
                :step="1"
                placeholder="X坐标"
              />
              <el-input-number
                v-model="form.center_z"
                :controls="false"
                :step="1"
                placeholder="Z坐标"
              />
            </div>
          </div>
        </el-form-item>

        <el-row :gutter="12" style="margin-top: 4px;">
          <el-col :span="14">
            <el-form-item label="高度范围" required style="margin-bottom: 2px;">
              <div class="height-inputs">
                <el-input-number
                  v-model="form.shape_y"
                  :controls="false"
                  :step="1"
                  :min="0"
                  :max="320"
                  placeholder="标记显示高度"
                  style="width: 110px;"
                />
                <span class="height-split">~</span>
                <el-input-number
                  v-model="form.center_y"
                  :controls="false"
                  :step="1"
                  placeholder="车站Y坐标"
                  style="width: 110px;"
                />
              </div>
            </el-form-item>
          </el-col>
          <el-col :span="10">
            <el-form-item style="margin-bottom: 2px;">
              <template #label>
                <span style="display: flex; align-items: center; gap: 4px;">
                  深度测试
                  <el-tooltip
                    placement="top"
                    effect="light"
                    :show-after="200"
                  >
                    <template #content>
                      <div style="max-width: 260px; line-height: 1.5;">
                        如果此值为 关闭，标记将始终渲染在所有其他（高分辨率）地形之上。<br/>
                        如果为 开启，当高分辨率图块位于标记前面时，它们将能够覆盖标记。
                      </div>
                    </template>
                    <el-icon :size="14" style="color: #909399; cursor: help;">
                      <QuestionFilled />
                    </el-icon>
                  </el-tooltip>
                </span>
              </template>
              <div class="depth-test-wrapper">
                <el-switch
                  v-model="form.depth_test"
                  active-text="开启"
                  inactive-text="关闭"
                />
              </div>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 顶点列表 -->
        <el-form-item label="顶点列表" class="form-item--full" style="margin-top: 8px;">
          <div class="vertices-editor">
            <div class="vertex-row vertex-header">
              <span>X</span>
              <span>Z</span>
              <span class="vertex-actions">操作</span>
            </div>
            <div
              v-for="(coord, index) in form.shape_coordinates"
              :key="index"
              class="vertex-row"
            >
              <el-input-number
                v-model="coord.x"
                :controls="false"
                :step="1"
              />
              <el-input-number
                v-model="coord.z"
                :controls="false"
                :step="1"
              />
              <div class="vertex-actions">
                <el-button
                  size="small"
                  :icon="Delete"
                  type="danger"
                  circle
                  @click="removeShapePoint(index)"
                  :disabled="form.shape_coordinates.length <= 3"
                />
              </div>
            </div>
            <div class="vertex-footer">
              <el-button size="small" type="primary" @click="addShapePoint">新增顶点</el-button>
            </div>
          </div>
        </el-form-item>
      </div>
    </el-card>
    
    <el-form-item label="所属线路" prop="line_ids" style="margin-bottom: 12px;">
      <el-select
        v-model="form.line_ids"
        multiple
        placeholder="选择所属线路（可多选）"
        style="width: 100%;"
        size="small"
        filterable
        clearable
      >
        <el-option
          v-for="line in lines"
          :key="line.id"
          :label="line.line_name"
          :value="line.id"
        >
          <div style="display: flex; align-items: center;">
            <span
              style="width: 12px; height: 12px; border-radius: 2px; margin-right: 8px;"
              :style="{ backgroundColor: `rgb(${line.line_color_r}, ${line.line_color_g}, ${line.line_color_b})` }"
            ></span>
            {{ line.line_name }}
            <span style="color: #8492a6; font-size: 12px; margin-left: 8px;">
              ({{ line.station_count || 0 }}站)
            </span>
          </div>
        </el-option>
      </el-select>
    </el-form-item>
    
    <el-form-item label="车站描述" style="margin-bottom: 12px;">
      <el-input
        v-model="form.description"
        type="textarea"
        :rows="2"
        placeholder="可选的车站描述信息"
        size="small"
      />
    </el-form-item>
    
    <div class="action-buttons">
      <el-button @click="$emit('cancel')">取消</el-button>
      <el-button 
        type="primary" 
        @click="submitForm" 
        :loading="loading"
        :disabled="!canSubmit"
      >
        {{ station ? '更新车站' : '添加车站' }}
      </el-button>
    </div>
  </el-form>
</div>
</template>

<script>
import { onMounted, ref, reactive, computed, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { Delete, Refresh, QuestionFilled } from '@element-plus/icons-vue'
import { stationApi, exportApi, buildingRegionApi } from '../api'
import configService from '../services/configService'
import pinyin from 'pinyin'

export default {
  name: 'StationForm',
  props: {
    station: {
      type: Object,
      default: null
    },
    lines: {
      type: Array,
      default: () => []
    },
    regions: {
      type: Array,
      default: () => []
    },
    defaultQuickAdd: {
      type: Boolean,
      default: true
    },
    defaultLineIds: {
      type: Array,
      default: () => []
    }
  },
  emits: ['success', 'cancel'],
  setup(props, { emit }) {
    const formRef = ref()
    const loading = ref(false)
    const shapeSize = ref(30)
    const shapeMode = ref('rectangle') // 'rectangle' | 'custom'
    // 状态码映射：0=运营，1=暂缓开通，2=停运，3=规划
    const DEFAULT_OPERATION_STATUS = 0
    const QUICK_DEFAULT_OPERATION_STATUS = 3
    const QUICK_DEFAULT_CENTER_Y = 64
    const quickCenterInput = ref('')

    const stationStatusOptions = [
      { label: '运营', value: 0, type: 'success' },
      { label: '暂缓开通', value: 1, type: 'warning' },
      { label: '停运', value: 2, type: 'danger' },
      { label: '规划', value: 3, type: 'info' }
    ]

    const isValidStatus = (status) => {
      // 支持数字状态码和字符串状态码
      if (typeof status === 'number') {
        return stationStatusOptions.some(option => option.value === status)
      }
      if (typeof status === 'string') {
        const statusMap = {
          '运营': 0,
          '暂缓开通': 1,
          '停运': 2,
          '规划': 3
        }
        return Object.prototype.hasOwnProperty.call(statusMap, status)
      }
      return false
    }
    
    // 将状态转换为数字状态码
    const normalizeStatus = (status) => {
      if (typeof status === 'number') {
        return stationStatusOptions.some(option => option.value === status) ? status : DEFAULT_OPERATION_STATUS
      }
      if (typeof status === 'string') {
        const statusMap = {
          '运营': 0,
          '暂缓开通': 1,
          '停运': 2,
          '规划': 3
        }
        return statusMap[status] !== undefined ? statusMap[status] : DEFAULT_OPERATION_STATUS
      }
      return DEFAULT_OPERATION_STATUS
    }

    const normalizeRegionId = (value) => {
      const parsed = Number(value)
      if (!Number.isFinite(parsed) || parsed <= 0) return null
      return parsed
    }

    const formatQuickCenterInput = () => {
      if (form.center_x === null || form.center_x === undefined ||
          form.center_z === null || form.center_z === undefined) {
        return ''
      }
      if (form.center_y === null || form.center_y === undefined) {
        return `${form.center_x} ${form.center_z}`
      }
      return `${form.center_x} ${form.center_y} ${form.center_z}`
    }

    const parseQuickCenterInput = (raw) => {
      const trimmed = (raw || '').trim()
      if (!trimmed) return null

      const parts = trimmed
        .replace(/[，]/g, ',')
        .split(/[,\s]+/)
        .map(part => part.trim())
        .filter(Boolean)

      if (parts.length !== 2 && parts.length !== 3) {
        throw new Error('中心坐标格式应为：x z 或 x y z')
      }

      const numbers = parts.map(part => Number(part))
      if (numbers.some(value => !Number.isFinite(value) || !Number.isInteger(value))) {
        throw new Error('中心坐标必须为整数')
      }

      if (numbers.length === 2) {
        return { x: numbers[0], y: QUICK_DEFAULT_CENTER_Y, z: numbers[1] }
      }

      return { x: numbers[0], y: numbers[1], z: numbers[2] }
    }
    
    // 矩形坐标数据
    const rectangleCoords = reactive({
      x1: null,
      z1: null,
      x2: null,
      z2: null
    })
    
    // 计算结果信息
    const calculatedInfo = reactive({
      width: 0,
      height: 0,
      area: 0
    })
    
    // 表单数据
    const form = reactive({
      chinese_name: '',
      english_name: '',
      station_code: '',
      center_x: 0,
      center_y: null,
      center_z: 0,
      shape_coordinates: [],
      shape_y: null,
      description: '',
      depth_test: false,
      line_ids: [],
      region_id: null,
      is_quick_added: false,
      operation_status: DEFAULT_OPERATION_STATUS
    })

    const regionOptions = ref(props.regions?.length ? [...props.regions] : [])
    const regionsLoading = ref(false)
    const availableRegions = computed(() => regionOptions.value || [])
    const selectedRegion = computed(() => {
      return availableRegions.value.find(region => normalizeRegionId(region.id) === normalizeRegionId(form.region_id)) || null
    })
    const regionSelectDisabled = computed(() => true)
    const regionPlaceholder = computed(() => {
      if (form.is_quick_added) {
        return '快速添加车站不选择行政区'
      }
      return selectedRegion.value ? '' : '后台自动匹配行政区'
    })
    const regionHintText = computed(() => {
      if (selectedRegion.value) {
        const englishPart = selectedRegion.value.english_name ? ` / ${selectedRegion.value.english_name}` : ''
        return `已自动匹配：${selectedRegion.value.chinese_name}${englishPart}`
      }
      if (form.is_quick_added) {
        return '快速添加车站暂不匹配行政区，保持为空。'
      }
      return '后台会根据中心坐标自动匹配行政区'
    })

    const loadRegions = async () => {
      if (regionsLoading.value) return
      if (props.regions && props.regions.length) {
        regionOptions.value = [...props.regions]
        return
      }
      regionsLoading.value = true
      try {
        const result = await buildingRegionApi.getAll()
        regionOptions.value = result.data || []
      } catch (error) {
        console.error('加载行政区失败:', error)
      } finally {
        regionsLoading.value = false
      }
    }

    const onRegionChange = (value) => {
      form.region_id = normalizeRegionId(value)
    }

    // 检查是否可以提交表单
    const canSubmit = computed(() => {
      // 快速创建模式可以提交
      if (form.is_quick_added) {
        return true
      }
      
      // 普通模式需要有坐标点才能提交
      return form.shape_coordinates && form.shape_coordinates.length >= 3
    })

    // 表单验证规则
    const rules = computed(() => {
      const baseRules = {
        chinese_name: [
          { required: true, message: '请输入车站名称', trigger: 'blur' }
        ],
        station_code: [
          {
            validator: (rule, value, callback) => {
              if (!value) {
                callback() // 车站代码不是必填项
                return
              }
              if (value.length !== 3) {
                callback(new Error('车站代码必须是3个字符'))
                return
              }
              if (!/^[A-Z]{3}$/.test(value)) {
                callback(new Error('车站代码必须是3个大写字母'))
                return
              }
              callback()
            },
            trigger: 'blur'
          }
        ],
        operation_status: [
          {
            validator: (rule, value, callback) => {
              if (!isValidStatus(value)) {
                callback(new Error('请选择车站状态'))
                return
              }
              callback()
            },
            trigger: 'change'
          }
        ]
      }
      
      // 如果是快速添加的车站，坐标不是必填项
      if (form.is_quick_added) {
        return baseRules
      }
      
      // 普通车站需要坐标
      return {
        ...baseRules,
        center_x: [
          { required: true, message: '请输入X坐标', trigger: 'blur' }
        ],
        center_y: [
          { required: true, message: '请输入Y坐标', trigger: 'blur' }
        ],
        center_z: [
          { required: true, message: '请输入Z坐标', trigger: 'blur' }
        ],
        shape_y: [
          { required: true, message: '请输入标记显示高度', trigger: 'blur' }
        ]
      }
    })
    
    // 生成英文名称
    const generateEnglishName = () => {
      if (form.chinese_name) {
        // 使用pinyin库将中文转换为拼音
        const pinyinArray = pinyin(form.chinese_name, {
          style: pinyin.STYLE_NORMAL, // 返回不带声调的拼音
          heteronym: false // 不返回多音字的所有读音
        })
        
        // 将拼音数组转换为PascalCase格式（首字母大写，其余小写）
        const joinedPinyin = pinyinArray
          .map(py => py[0]) // 取每个字的第一个拼音
          .join('') // 先连接所有拼音
        
        // 对整个字符串进行PascalCase处理：首字母大写，其余小写
        const englishName = joinedPinyin.charAt(0).toUpperCase() + joinedPinyin.slice(1).toLowerCase()
        
        form.english_name = englishName
      }
    }

    // 中文名称变化时的处理
    const onChineseNameChange = () => {
      // 实时生成英文名，但不覆盖用户手动输入的内容
      if (form.chinese_name && !form.english_name) {
        generateEnglishName()
      }
    }

    // 形状模式切换
    const onShapeModeChange = (mode) => {
      // 切换模式时不自动生成坐标，用户需要手动输入并生成
      if (mode === 'rectangle') {
        // 快速矩形模式：用户需要输入对角线坐标并点击生成按钮
      } else if (mode === 'custom') {
        // 自定义模式：用户需要手动添加顶点
      }
    }

    // 快速添加开关切换
    const setOperationStatus = (status) => {
      if (!isValidStatus(status)) return
      form.operation_status = status
      if (formRef.value) {
        formRef.value.validateField('operation_status')
      }
    }

    const onQuickAddToggle = () => {
      if (form.is_quick_added) {
        // 进入快速模式：放开对形状和坐标的强制校验，界面也已隐藏
        // 不清空数据，避免误操作丢失；提交时后端会按 is_quick_added 保存
        if (!quickCenterInput.value.trim() && form.center_y !== null && form.center_y !== undefined) {
          quickCenterInput.value = formatQuickCenterInput()
        }
        if (normalizeStatus(form.operation_status) === DEFAULT_OPERATION_STATUS) {
          form.operation_status = QUICK_DEFAULT_OPERATION_STATUS
        }
        form.region_id = null
      } else {
        // 切回普通模式：用户需要手动输入坐标并生成
        // 不再自动生成默认矩形
        if (normalizeStatus(form.operation_status) === QUICK_DEFAULT_OPERATION_STATUS) {
          form.operation_status = DEFAULT_OPERATION_STATUS
        }
        form.region_id = null
      }

      if (formRef.value) {
        formRef.value.validateField('operation_status')
      }
    }

    // 车站代码输入处理
    const onStationCodeInput = (value) => {
      // 只允许字母，自动转换为大写
      const filtered = value.replace(/[^a-zA-Z]/g, '').toUpperCase()
      // 限制长度为3
      form.station_code = filtered.slice(0, 3)
    }

    // 车站代码失焦处理
    const onStationCodeBlur = () => {
      // 确保长度为3，不足的用空格填充（可选）
      if (form.station_code && form.station_code.length < 3) {
        // 可以选择不填充，让用户自己决定
        // form.station_code = form.station_code.padEnd(3, ' ')
      }
    }

    // 计算矩形形状（核心逻辑）
    const calculateRectangleShape = () => {
      // 验证是否填入了所有四个坐标点
      if (rectangleCoords.x1 === null || rectangleCoords.x1 === '' ||
          rectangleCoords.z1 === null || rectangleCoords.z1 === '' ||
          rectangleCoords.x2 === null || rectangleCoords.x2 === '' ||
          rectangleCoords.z2 === null || rectangleCoords.z2 === '') {
        ElMessage.warning('请输入完整的矩形对角线坐标（x1, z1, x2, z2）')
        return
      }

      // 转换为数字
      const x1 = parseFloat(rectangleCoords.x1)
      const z1 = parseFloat(rectangleCoords.z1)
      const x2 = parseFloat(rectangleCoords.x2)
      const z2 = parseFloat(rectangleCoords.z2)

      // 验证是否为有效数字
      if (isNaN(x1) || isNaN(z1) || isNaN(x2) || isNaN(z2)) {
        ElMessage.warning('请输入有效的数字坐标')
        return
      }

      // 如果两个点相同，则不计算
      if (x1 === x2 && z1 === z2) {
        ElMessage.warning('矩形对角线的两个点不能相同')
        return
      }

      // 确保坐标顺序正确（x1 < x2, z1 < z2）
      const minX = Math.min(x1, x2)
      const maxX = Math.max(x1, x2)
      const minZ = Math.min(z1, z2)
      const maxZ = Math.max(z1, z2)

      // 计算中心点坐标
      form.center_x = Math.round((minX + maxX) / 2)
      form.center_z = Math.round((minZ + maxZ) / 2)

      // 计算矩形尺寸和面积
      calculatedInfo.width = maxX - minX
      calculatedInfo.height = maxZ - minZ
      calculatedInfo.area = calculatedInfo.width * calculatedInfo.height

      // 生成形状坐标（按逆时针顺序，绝对坐标）
      // 根据用户输入的对角线点生成完整的矩形四个顶点
      form.shape_coordinates = [
        { x: minX, z: minZ }, // 左下角 (起始点)
        { x: maxX, z: minZ }, // 右下角
        { x: maxX, z: maxZ }, // 右上角
        { x: minX, z: maxZ } // 左上角
      ]

      ElMessage.success('矩形坐标生成成功')
    }

    // 从现有车站数据反推矩形坐标
    const calculateRectangleCoordsFromShape = () => {
      if (form.shape_coordinates.length === 4) {
        // 假设是矩形，计算边界（现在是绝对坐标）
        const xs = form.shape_coordinates.map(p => p.x)
        const zs = form.shape_coordinates.map(p => p.z)
        
        rectangleCoords.x1 = Math.min(...xs)
        rectangleCoords.z1 = Math.min(...zs)
        rectangleCoords.x2 = Math.max(...xs)
        rectangleCoords.z2 = Math.max(...zs)
        
        calculatedInfo.width = rectangleCoords.x2 - rectangleCoords.x1
        calculatedInfo.height = rectangleCoords.z2 - rectangleCoords.z1
        calculatedInfo.area = calculatedInfo.width * calculatedInfo.height
      }
    }
    
    // 生成基础矩形形状（用于自定义模式的默认形状）
    const generateRectShape = () => {
      const halfSize = shapeSize.value / 2
      form.shape_coordinates = [
        { x: form.center_x - halfSize, z: form.center_z - halfSize },
        { x: form.center_x + halfSize, z: form.center_z - halfSize },
        { x: form.center_x + halfSize, z: form.center_z + halfSize },
        { x: form.center_x - halfSize, z: form.center_z + halfSize }
      ]
    }
    
    // 更新形状坐标（当中心坐标改变时）
    const updateShapeCoordinates = () => {
      if (form.shape_coordinates.length === 4) {
        // 假设是矩形，更新坐标（绝对坐标）
        const halfSize = shapeSize.value / 2
        form.shape_coordinates = [
          { x: form.center_x - halfSize, z: form.center_z - halfSize },
          { x: form.center_x + halfSize, z: form.center_z - halfSize },
          { x: form.center_x + halfSize, z: form.center_z + halfSize },
          { x: form.center_x - halfSize, z: form.center_z + halfSize }
        ]
      }
    }
    
    // 添加形状顶点
    const addShapePoint = () => {
      form.shape_coordinates.push({
        x: form.center_x,
        z: form.center_z
      })
    }
    
    // 移除形状顶点
    const removeShapePoint = (index) => {
      if (form.shape_coordinates.length > 3) {
        form.shape_coordinates.splice(index, 1)
      }
    }
    
    // 重置形状
    const resetShape = () => {
      form.shape_coordinates = []
      if (shapeMode.value === 'rectangle') {
        // 重置矩形坐标输入框
        rectangleCoords.x1 = null
        rectangleCoords.z1 = null
        rectangleCoords.x2 = null
        rectangleCoords.z2 = null
        // 不自动生成，用户需要手动输入并点击生成
      } else {
        // 自定义模式下也不自动生成，用户需要手动添加顶点
      }
    }
    
    // 验证形状
    const validateShape = () => {
      if (form.shape_coordinates.length < 3) {
        ElMessage.warning('形状至少需要3个顶点')
      }
    }
    
    // 提交表单
    const submitForm = async () => {
      if (!formRef.value) return
      
      try {
        await formRef.value.validate()
        
        // 快速添加的车站不需要验证形状坐标
        const isQuickAdded = form.is_quick_added
        if (!isQuickAdded && form.shape_coordinates.length < 3) {
          ElMessage.error('形状至少需要3个顶点')
          return
        }
        
        loading.value = true
        
        // 生成描述信息
        if (!form.description) {
          form.description = `${form.chinese_name}${form.english_name ? '|' + form.english_name : ''}`
        }
        
        // 如果为快速添加，清空坐标相关字段，后端以空值保存
        if (form.is_quick_added) {
          try {
            const parsed = parseQuickCenterInput(quickCenterInput.value)
            if (parsed) {
              form.center_x = parsed.x
              form.center_y = parsed.y
              form.center_z = parsed.z
            } else {
              form.center_x = null
              form.center_y = null
              form.center_z = null
            }
          } catch (error) {
            ElMessage.error(error.message || '中心坐标格式不正确')
            loading.value = false
            return
          }
          form.shape_coordinates = []
          form.shape_y = null
          form.region_id = null
        } else {
          // 行政区由后台根据中心点自动匹配
          form.region_id = null
        }
        
        form.operation_status = normalizeStatus(form.operation_status)

        if (props.station) {
          await stationApi.update(props.station.id, form)
        } else {
          await stationApi.create(form)
        }
        
        // 自动同步到 BlueMap 服务器（静默执行）
        syncToBlueMap()
        
        emit('success')
      } catch (error) {
        console.error('保存车站失败:', error)
      } finally {
        loading.value = false
      }
    }
    
    // 自动同步到 BlueMap 服务器
    const syncToBlueMap = async () => {
      try {
        console.log('🔄 自动同步配置到 BlueMap 服务器...')
        
        // 获取当前保存的全局配置
        const apiConfig = configService.getApiConfig()
        
        // 使用全局配置进行推送
        await exportApi.pushToBlueMap({ 
          include_config_header: true,
          ...apiConfig
        })
        
        console.log('✅ BlueMap 配置已自动更新（使用全局默认配置）')
      } catch (error) {
        // 静默处理错误，不影响主流程
        console.warn('⚠️ BlueMap 自动同步失败（不影响车站保存）:', error.message)
      }
    }
    
    // 初始化表单数据
    const initForm = () => {
      if (props.station) {
        Object.assign(form, {
          ...props.station,
          station_code: props.station.station_code || '',
          line_ids: props.station.lines ? props.station.lines.map(l => l.line_id) : [],
          is_quick_added: Boolean(props.station.is_quick_added),
          operation_status: normalizeStatus(props.station.operation_status),
          region_id: normalizeRegionId(props.station.region_id)
        })

        if (form.is_quick_added) {
          quickCenterInput.value = formatQuickCenterInput()
        } else {
          quickCenterInput.value = ''
        }
        
        // 根据现有形状判断模式
        if (!form.is_quick_added) {
          if (form.shape_coordinates && form.shape_coordinates.length === 4) {
            // 尝试判断是否为规则矩形
            calculateRectangleCoordsFromShape()
            shapeMode.value = 'rectangle'
          } else {
            // 默认使用快速矩形模式，即使已有自定义形状
            shapeMode.value = 'rectangle'
            // 用户需要手动输入坐标并生成，不再自动生成
          }
        }
      } else {
        // 重置表单
        Object.assign(form, {
          chinese_name: '',
          english_name: '',
          station_code: '',
          center_x: 0,
          center_y: null,
          center_z: 0,
          shape_coordinates: [],
          shape_y: null,
          description: '',
          depth_test: false,
          line_ids: props.defaultLineIds || [],
          region_id: null,
          is_quick_added: props.defaultQuickAdd,
          operation_status: props.defaultQuickAdd ? QUICK_DEFAULT_OPERATION_STATUS : DEFAULT_OPERATION_STATUS
        })

        quickCenterInput.value = ''
        
        // 重置矩形坐标输入
        rectangleCoords.x1 = null
        rectangleCoords.z1 = null
        rectangleCoords.x2 = null
        rectangleCoords.z2 = null
        
        // 重置计算信息
        calculatedInfo.width = 0
        calculatedInfo.height = 0
        calculatedInfo.area = 0
        
        if (!form.is_quick_added) {
          // 默认使用快速矩形模式
          shapeMode.value = 'rectangle'
        }
      }
    }
    
    watch(() => props.station, initForm, { immediate: true })

    watch(() => props.regions, (newRegions) => {
      if (Array.isArray(newRegions) && newRegions.length) {
        regionOptions.value = [...newRegions]
      }
    })
    
    // 监听默认线路ID变化，当没有现有车站时更新表单
    watch(() => props.defaultLineIds, (newDefaultLineIds) => {
      if (!props.station && newDefaultLineIds && newDefaultLineIds.length > 0) {
        form.line_ids = [...newDefaultLineIds]
      }
    })

    onMounted(() => {
      if (!regionOptions.value.length) {
        loadRegions()
      }
    })
    
    return {
      formRef,
      loading,
      shapeSize,
      shapeMode,
      rectangleCoords,
      calculatedInfo,
      form,
      rules,
      canSubmit,
      generateEnglishName,
      onChineseNameChange,
      onShapeModeChange,
      onQuickAddToggle,
      onStationCodeInput,
      onStationCodeBlur,
      calculateRectangleShape,
      calculateRectangleCoordsFromShape,
      generateRectShape,
      updateShapeCoordinates,
      addShapePoint,
      removeShapePoint,
      resetShape,
      validateShape,
      submitForm,
      stationStatusOptions,
      setOperationStatus,
      availableRegions,
      regionsLoading,
      selectedRegion,
      regionSelectDisabled,
      regionPlaceholder,
      regionHintText,
      onRegionChange,
      // Icons
      Delete,
      Refresh,
      QuestionFilled,
      quickCenterInput
    }
  }
}
</script>

<style scoped>
.station-form-modern {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.station-form-modern__form {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-section {
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.08);
  padding: 12px 16px;
  transition: box-shadow 0.3s ease, transform 0.3s ease;
}

.form-section:hover {
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.12);
  transform: translateY(-2px);
}

.form-section__grid {
  margin-bottom: 4px;
}

.form-item--compact {
  margin-bottom: 0 !important;
}

.form-item--compact :deep(.el-form-item__label) {
  color: #1f2937;
  font-weight: 600;
  font-size: 12px;
  line-height: 1.2;
  margin-bottom: 2px;
  padding-bottom: 2px;
}

.form-item--compact :deep(.el-input__wrapper) {
  border-radius: 10px;
  padding: 2px 8px;
}

.station-form-modern :deep(.el-input__inner) {
  height: 24px;
  line-height: 24px;
  font-size: 13px;
}

.station-form-modern :deep(.el-switch.is-checked .el-switch__core) {
  background-color: #f47f4c !important;
  border-color: #f47f4c !important;
}

.station-form-modern :deep(.el-radio-button.is-active .el-radio-button__inner) {
  background-color: rgba(244, 127, 76, 0.12) !important;
  border-color: rgba(244, 127, 76, 0.32) !important;
  color: #f47f4c !important;
  box-shadow: -1px 0 0 0 rgba(244, 127, 76, 0.32) !important;
}

.station-form-modern :deep(.el-input),
.station-form-modern :deep(.el-select),
.station-form-modern :deep(.el-input-number) {
  --el-input-border-color: rgba(244, 127, 76, 0.18);
  --el-input-hover-border-color: rgba(244, 127, 76, 0.28);
  --el-input-focus-border-color: rgba(244, 127, 76, 0.48);
  --el-input-focus-border: rgba(244, 127, 76, 0.48);
  --el-select-input-focus-border-color: rgba(244, 127, 76, 0.48);
  --el-select-input-border-color-hover: rgba(244, 127, 76, 0.28);
  --el-color-primary: #f47f4c;
}

.station-form-modern :deep(.el-form-item) {
  margin-bottom: 4px;
}

.station-form-modern :deep(.el-form-item__label) {
  line-height: 1.2;
  margin-bottom: 2px;
  padding-bottom: 2px;
  font-size: 12px;
}

.quick-toggle-inline {
  background: rgba(244, 127, 76, 0.08);
  border-radius: 10px;
  padding: 4px 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 24px;
}

.quick-toggle-inline :deep(.el-switch__core) {
  transform: scale(0.85);
}

.quick-center-item :deep(.el-form-item__content) {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.quick-center-hint {
  font-size: 12px;
  color: rgba(100, 116, 139, 0.95);
  line-height: 1.2;
}

.status-row {
  margin-top: 8px;
  margin-bottom: 0;
}

.status-region-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: stretch;
}

.status-col {
  flex: 1 1 360px;
  min-width: 280px;
}

.region-col {
  flex: 0 0 280px;
  max-width: 340px;
}

.status-row :deep(.el-form-item) {
  margin-bottom: 0 !important;
}

.region-row {
  margin-top: 8px;
}

.region-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.region-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.region-option__name {
  font-weight: 600;
}

.region-option__sub {
  color: #909399;
  font-size: 12px;
}

.region-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6b7280;
  font-size: 12px;
  line-height: 1.2;
}

.region-hint__text {
  display: inline-block;
}

.quick-status-row {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  align-items: stretch;
  margin-top: 12px;
}

.quick-status-row__toggle {
  flex: 0 0 180px;
  margin-bottom: 0 !important;
}

.quick-status-row__status {
  flex: 1;
  margin-bottom: 0 !important;
  max-width: calc(100% - 200px);
}

.quick-toggle {
  background: rgba(244, 127, 76, 0.08);
  border-radius: 14px;
  padding: 8px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 36px;
}

.quick-toggle :deep(.el-switch__core) {
  transform: scale(0.9);
}

.generate-english-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  margin-left: 4px;
  border: 1px solid rgba(244, 127, 76, 0.22);
  border-radius: 6px;
  background: rgba(244, 127, 76, 0.12);
  color: #f47f4c;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  font-size: 14px;
}

.generate-english-btn:hover {
  background: rgba(244, 127, 76, 0.2);
  border-color: rgba(244, 127, 76, 0.32);
  color: #e86a32;
}

.generate-english-btn:active {
  background: rgba(244, 127, 76, 0.32);
  transform: scale(0.96);
}

.generate-english-btn:focus-visible {
  outline: 2px solid rgba(244, 127, 76, 0.45);
  outline-offset: 2px;
}

.status-selector {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 8px;
  padding: 8px 12px;
  background: rgba(244, 127, 76, 0.06);
  border-radius: 14px;
}

.status-chip {
  border: none;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(99, 102, 241, 0.12);
  color: #4b5563;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
}

.status-chip:hover {
  transform: translateY(-1px);
}

.status-chip__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.status-chip.is-active {
  color: #ffffff;
  box-shadow: 0 10px 20px rgba(244, 127, 76, 0.15);
}

.status-chip--success {
  background: rgba(16, 185, 129, 0.18);
  color: #047857;
}

.status-chip--success.is-active {
  background: linear-gradient(135deg, #34d399 0%, #059669 100%);
}

.status-chip--warning {
  background: rgba(251, 191, 36, 0.2);
  color: #b45309;
}

.status-chip--warning.is-active {
  background: linear-gradient(135deg, #fbbf24 0%, #d97706 100%);
}

.status-chip--danger {
  background: rgba(248, 113, 113, 0.18);
  color: #b91c1c;
}

.status-chip--danger.is-active {
  background: linear-gradient(135deg, #f87171 0%, #dc2626 100%);
}

.status-chip--info {
  background: rgba(59, 130, 246, 0.18);
  color: #1d4ed8;
}

.status-chip--info.is-active {
  background: linear-gradient(135deg, #60a5fa 0%, #2563eb 100%);
}

.quick-mode-alert {
  margin-top: 4px;
}

.shape-designer {
  margin: 6px 0;
}

.rectangle-inputs-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: nowrap;
}

.rectangle-inputs {
  display: flex;
  gap: 6px;
  padding: 6px 10px;
  flex-wrap: nowrap;
  border-radius: 10px;
  background: rgba(244, 127, 76, 0.08);
  align-items: center;
  flex: 1 1 auto;
  justify-content: flex-start;
}

.rectangle-inputs .el-input-number {
  width: 76px;
}

.rectangle-inputs-row .el-button {
  flex-shrink: 0;
  min-width: 88px;
}

@media (max-width: 768px) {
  .rectangle-inputs {
    flex-wrap: wrap;
  }

  .rectangle-inputs-row {
    align-items: stretch;
    flex-wrap: wrap;
  }

  .rectangle-inputs-row .el-button {
    width: 100%;
    justify-content: center;
  }
}

.depth-test-wrapper {
  background: rgba(244, 127, 76, 0.08);
  border-radius: 10px;
  padding: 6px 10px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: 40px;
}

.depth-test-wrapper .el-switch {
  margin: 0;
}

.height-inputs {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 10px;
  background: rgba(244, 127, 76, 0.08);
}

.height-split {
  color: #f47f4c;
  font-weight: 700;
  font-size: 16px;
  padding: 0 4px;
  user-select: none;
}

.vertices-editor {
  border: 1px solid rgba(244, 127, 76, 0.16);
  border-radius: 12px;
  overflow: hidden;
  background: #ffffff;
}

.vertex-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-bottom: 1px solid rgba(244, 127, 76, 0.12);
  transition: background 0.2s ease;
}

.vertex-row:last-of-type {
  border-bottom: none;
}

.vertex-row:not(.vertex-header):not(.vertex-footer):hover {
  background: rgba(244, 127, 76, 0.04);
}

.vertex-row .el-input-number {
  flex: 1;
}

.vertex-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 50px;
}

.vertex-header {
  background: rgba(244, 127, 76, 0.12);
  font-weight: 600;
  font-size: 12px;
  color: #2c3e50;
  padding: 6px 12px;
}

.vertex-header span {
  flex: 1;
  text-align: center;
}

.vertex-footer {
  background: rgba(244, 127, 76, 0.08);
  padding: 10px;
  text-align: center;
  border-top: 1px solid rgba(244, 127, 76, 0.16);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  padding: 4px 0;
  min-height: 24px;
}

.rectangle-mode, .custom-mode {
  padding: 4px 0;
}

.coord-card {
  background: rgba(244, 127, 76, 0.08);
  border: 1px solid rgba(244, 127, 76, 0.16);
}

.coord-card h4 {
  margin: 0 0 4px 0;
  color: #2c3e50;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
}

.result-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  margin-top: 4px;
}

.result-card h4 {
  margin: 0 0 16px 0;
  color: white;
  font-size: 16px;
  font-weight: 600;
}

.result-card :deep(.el-statistic__number) {
  color: white !important;
}

.result-card :deep(.el-statistic__title) {
  color: rgba(255, 255, 255, 0.8) !important;
}

.result-card :deep(.el-statistic__suffix) {
  color: rgba(255, 255, 255, 0.9) !important;
}

.shape-editor {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 12px;
  background-color: #fafafa;
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
}

.el-table {
  margin-top: 8px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .form-section {
    padding: 16px;
  }

  .quick-status-row {
    flex-direction: column;
  }

  .quick-status-row__toggle,
  .quick-status-row__status {
    flex: 1 1 100%;
    max-width: 100%;
  }

.status-selector {
  justify-content: flex-start;
  flex-wrap: wrap;
}

  .shape-designer {
    margin: 16px 0;
  }
  
  .card-header {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }
  
  .card-header span {
    text-align: center;
    font-size: 16px;
  }
  
  .rectangle-mode, .custom-mode {
    padding: 8px 0;
  }
  
  .coord-card {
    margin-bottom: 12px;
  }
  
  .coord-card h4 {
    font-size: 13px;
    margin-bottom: 8px;
  }
  
  .result-card {
    margin-top: 16px;
    padding: 16px;
  }
  
  .result-card h4 {
    font-size: 14px;
    margin-bottom: 12px;
  }
  
  .action-buttons {
    flex-direction: column;
    gap: 8px;
    margin-top: 16px;
  }
  
.action-buttons .el-button {
  width: 100%;
}

/* 表单行优化 */
.el-row {
  margin: 0 !important;
}

  .el-col {
    margin-bottom: 8px;
  }
  
  /* 输入框优化 */
  .el-input, .el-input-number {
    font-size: 14px;
  }
  
  /* 表格优化 */
  .el-table {
    font-size: 12px;
  }
  
  .el-table .el-table__cell {
    padding: 8px 4px;
  }
}

@media (max-width: 480px) {
  .form-section {
    padding: 14px;
  }

  .status-selector {
    gap: 8px;
    justify-content: flex-start;
  }

  .status-chip {
    padding: 6px 12px;
    font-size: 11px;
  }

  .shape-designer {
    margin: 12px 0;
  }
  
  .coord-card h4 {
    font-size: 12px;
  }
  
  .result-card {
    padding: 12px;
  }
  
  .result-card h4 {
    font-size: 13px;
  }
  
  /* 更小的输入框 */
  .el-input, .el-input-number {
    font-size: 13px;
  }
  
  /* 表格进一步优化 */
  .el-table {
    font-size: 11px;
  }
  
  .el-table .el-table__cell {
    padding: 6px 2px;
  }
  
  /* 按钮优化 */
  .el-button {
    font-size: 13px;
    padding: 8px 12px;
  }
}

</style>
